import { useMediaQuery } from '@ag1/ui';
import { useScrollPaddingTopForType } from 'atoms/scrollPaddingTopAtom';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { ImageAndExpandableListV2 as ImageAndExpandableListProps } from 'types/studio/studio';
import { StudioHotspotImageV2 } from '../StudioHotspotImage/StudioHotspotImage';
import styles from './ImageAndExpandableListV2.module.scss';

interface Props {
  sectionRef: React.MutableRefObject<HTMLDivElement | null>;
  contentLeft: boolean;
  image: ImageAndExpandableListProps['image'];
  visibleOnPageLoad: boolean;
}

export const ScrollableImage = ({ sectionRef, contentLeft, image, visibleOnPageLoad }: Props) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const navigationBottom = useScrollPaddingTopForType(null, 'raw');
  const atMedium = useMediaQuery('m');

  const handleScroll = useCallback(() => {
    if (!atMedium) return;

    const section = sectionRef.current;
    const imageComponent = imageRef.current;

    if (!section || !imageComponent) return;

    const coords = section.getBoundingClientRect();

    imageComponent.style.height = `calc(100vh - ${navigationBottom}px)`;

    if (coords.y <= navigationBottom) {
      requestAnimationFrame(() => {
        imageComponent.style.position = 'fixed';
        imageComponent.style.top = `${navigationBottom}px`;
      });
    } else {
      requestAnimationFrame(() => {
        imageComponent.style.position = 'absolute';
        imageComponent.style.top = '0px';
      });
    }
  }, [navigationBottom, sectionRef, atMedium]);

  useEffect(() => {
    if (atMedium) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationBottom, atMedium]);

  return (
    <div className={styles.hotspotImage}>
      <div
        className={clsx(
          styles.hotspotImageContainerDesktop,
          contentLeft ? styles.imageRight : styles.imageLeft
        )}
        ref={imageRef}>
        <StudioHotspotImageV2
          image={image}
          className={styles.hotspotIllustrationDesktop}
          sizes={{ xxs: 12, m: 6 }}
          eagerLoading={visibleOnPageLoad}
        />
      </div>
    </div>
  );
};
